import React from "react";
import { Flex } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { qoriaColours } from "@/theme/colours";
import { fonts } from "@/theme/fonts";
import { useClasswizeConfigStore } from "@/stores/classwizeConfigStore";
import { RuleAction } from "../types";
import { SlidingToggle } from "../SlidingToggle";

interface Props {
	value: RuleAction;
	onChange: (action: RuleAction) => void;
	/** Renders a smaller, compact variant used inside a rule row. */
	size?: "md" | "sm";
	isDisabled?: boolean;
}

function sizeStyles(size: "md" | "sm" = "md"): { height: number; px: number; fontSize: "sm" | "md" } {
	if (size === "sm") {
		return { height: 8, px: 3, fontSize: "sm" };
	}
	return { height: 10, px: 4, fontSize: "md" };
}

/**
 * Segmented "Block" / "Allow" toggle. The track is white with a hairline border so
 * it reads as a control on both white and tinted rows; the selected option is filled
 * with its state colour, red for Block and green for Allow.
 *
 * For schools with `create_blocking_rules_only`, this is a static pill: Block for new and
 * existing block rules, Allow only when the rule was already an allow rule.
 */
export function BlockAllowToggle(props: Props): JSX.Element {
	const { t } = useTranslation();
	const { value, onChange, size = "md", isDisabled = false } = props;
	const blockingRulesOnly = useClasswizeConfigStore((state) => state.data.create_blocking_rules_only) === true;

	if (blockingRulesOnly) {
		return (
			<ActionOnlyPill action={value} size={size} label={value === "allow" ? t("rulesLibrary.form.allow") : t("rulesLibrary.form.block")} />
		);
	}

	return (
		<SlidingToggle
			size={size}
			isDisabled={isDisabled}
			selectedIndex={value === "allow" ? 1 : 0}
			options={[
				{
					label: t("rulesLibrary.form.block"),
					icon: "fa-ban",
					activeBg: qoriaColours.brand.danger[10],
					activeColour: qoriaColours.brand.danger[500],
					onSelect: () => onChange("block"),
				},
				{
					label: t("rulesLibrary.form.allow"),
					icon: "fa-circle-check",
					activeBg: qoriaColours.brand.success[10],
					activeColour: qoriaColours.brand.success[500],
					onSelect: () => onChange("allow"),
				},
			]}
		/>
	);
}

/**
 * Static Block or Allow state shown instead of the toggle when the school cannot create
 * new allow rules. Existing allow rules keep the Allow appearance.
 */
function ActionOnlyPill(props: { action: RuleAction; label: string; size?: "md" | "sm" }): JSX.Element {
	const sizing = sizeStyles(props.size);
	const blocked = props.action === "block";

	return (
		<Flex
			align="center"
			justify="center"
			alignSelf="flex-start"
			gap={2}
			height={sizing.height}
			px={sizing.px}
			fontSize={sizing.fontSize}
			rounded="lg"
			border="1px solid"
			borderColor={qoriaColours.brand.neutral[100]}
			bg={blocked ? qoriaColours.brand.danger[10] : qoriaColours.brand.success[10]}
			color={blocked ? qoriaColours.brand.danger[500] : qoriaColours.brand.success[500]}
			fontFamily={fonts.body}
			fontWeight={500}
		>
			<ChakraFAIcon icon={blocked ? "fa-ban" : "fa-circle-check"} variant="solid" boxSize={4} />
			{props.label}
		</Flex>
	);
}
